<template>
	<popup v-model="isActive" zoom-transition>
		<card max-width="900" width="100%" :height="$breakpoint.xsOnly ? '100%' : null" no-spacing xs-tile>
			<card-text class="p-0" fill>
				<div class="w-full h-full flex flex-column items-center justify-center relative">
					<slider v-model="currentIndex" with-keyboard-navigation :show-arrows="props.items.length > 1" visible-arrows :slides-per-view="1">
						<template v-for="(item, itemIndex) in props.items" :key="itemIndex">
							<slider-item v-if="item[props.sourceProp] && itemIndex >= (currentIndex - 10) && itemIndex <= (currentIndex + 10)" :model-value="itemIndex" class="bg-gray-150">
								<div class="w-full flex items-center justify-center">
									<video v-if="props.typeProp && item[props.typeProp] === 'video'" :height="modalImageHeight" controls controlsList="nodownload" class="min-w-full w-full">
										<source :src="typeof item[props.sourceProp] === 'string' ? item[props.sourceProp] : item[props.sourceProp].url" :type="parseVideoMimeType(item)"/>
										Your browser does not support the video tag.
									</video>
									<cdn-image v-else :size="{height: modalImageHeight, ratio: item.ratio ?? 1}" :src="item[props.sourceProp]"
											   prevent-download/>
								</div>
							</slider-item>
						</template>
					</slider>
					<slot/>
				</div>
			</card-text>

			<wrapper :between="!!$slots.buttons" grow :center="!$slots.buttons" class="px-16 py-16 lg:px-24 border-box border-t">
				<btn @click="isActive=false" large>{{ $t('action.close') }}</btn>
				<slot name="buttons"/>
			</wrapper>
		</card>
	</popup>
</template>

<script setup lang="ts">
import {ref, watch} from 'vue';

const props = withDefaults(defineProps<{
	modelValue?: boolean,
	items: Array<{ [key: string]: any }>,
	sourceProp?: string,
	typeProp?: string,
	index?: number,

	// The height of the default slot content
	// Required to subtract it from the window height when calculating the image height
	// Cannot be calculated automatically as the slot content might not be rendered when opening the modal
	defaultSlotHeight?: number
}>(), {
	sourceProp: 'src',
	typeProp: 'type',
	index: 0,
	defaultSlotHeight: 0
});

const emit = defineEmits(['update:index', 'update:modelValue']);

const modalImageHeight = ref(0);
const currentIndex = ref(props.index);
const isActive = ref(props.modelValue ?? false);

watch(isActive, (newValue) => {
	if (newValue) {
		const buttonWrapperHeight = 81;
		const windowHeight = window.innerHeight || document.documentElement.clientHeight;
		modalImageHeight.value = Math.min(windowHeight - buttonWrapperHeight - props.defaultSlotHeight, 720);
	}
	emit('update:modelValue', newValue);
});
watch(() => props.modelValue, (newValue) => {
	isActive.value = newValue;
});

watch(() => props.index, (newValue) => {
	currentIndex.value = newValue;
});

watch(currentIndex, (newValue) => {
	emit('update:index', newValue);
});

/**
 * Only specific video mime types are playable in the browser whereby we need to modify it.
 * We can not use the gallery media mime type as it might differ for previews and original sources.
 */

function parseVideoMimeType(item) {
	const url = typeof item[props.sourceProp] === 'string' ? item[props.sourceProp] : item[props.sourceProp].url;
	const urlParts = url.split('.');
	const extension = urlParts[urlParts.length - 1];
	const fallback = 'video/mp4';
	const extensionMapping = {
		'mp4': 'video/mp4',
		'webm': 'video/webm',
		'ogg': 'video/ogg',
	};

	if (extensionMapping[extension]) {
		return extensionMapping[extension];
	}

	return fallback;
}

</script>